import { ArrowRight, History, Info, MapPin, Truck, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { MyFortAgentItem } from "@/api/types";
import { C } from "@/components/modal/home";
import { DAY_RAT_MAP, getAgentTierKey, getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS } from "@/config/env";
import { showError, showInfo } from "@/lib/toast";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { AGENT_IMAGE_URL_ACTIVE } from "./constants";

export type HomeMyAgentActivatedCardV2Props = {
  agent: MyFortAgentItem;
  onAddress: (agent: MyFortAgentItem) => void;
  onDeliveryStatus: (agent: MyFortAgentItem) => void;
  onGoManage: () => void;
  onIncomeHistory: (agent: MyFortAgentItem) => void;
};

export function HomeMyAgentActivatedCardV2({
  agent,
  onAddress,
  onDeliveryStatus,
  onGoManage,
  onIncomeHistory,
}: HomeMyAgentActivatedCardV2Props) {
  const { t } = useTranslation();
  const { openActiveDetail } = useMyAgentsUiStore();
  const tierLabel = t(getAgentTierKey(agent.name));
  const todayEarnedU = formatThousands(
    formatEtherWithDecimals(agent.todayStaticPayoutUsdt ?? "0", 2),
    2
  );
  const rateWanFen = Number(agent.todayStaticPayoutRateBpms ?? 0);
  const dailyRate = `${rateWanFen / 10000}%`;
  const aifValueDisplay = formatUsdPrefix(
    formatThousands(formatEtherWithDecimals(agent.aifValueUsdt, 0), 0)
  );

  const showFeatureNotAvailable = () =>
    showError(t("toast.common.featureNotAvailable"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl overflow-hidden relative"
      style={{
        background:
          "linear-gradient(135deg, rgba(34,197,94,0.03) 0%, #0c1020 40%, #080c18 100%)",
        border: "1px solid rgba(34,197,94,0.07)",
        boxShadow: "0 2px 20px rgba(34,197,94,0.02)",
      }}
    >
      <motion.div
        className="h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 10%, rgba(34,197,94,0.08) 50%, transparent 90%)",
        }}
      />

      <div className="flex" style={{ minHeight: 0 }}>
        <button
          type="button"
          className="relative flex flex-col items-center justify-center overflow-hidden cursor-pointer border-0 p-0 text-left"
          style={{
            width: "42%",
            minHeight: "200px",
            backgroundImage:
              "radial-gradient(ellipse at center 60%, rgba(147,51,234,0.15) 0%, rgba(34,197,94,0.08) 35%, transparent 70%)",
          }}
          onClick={showFeatureNotAvailable}
        >
          <div
            className="absolute inset-0 pointer-events-none z-20"
            style={{
              background: `
                linear-gradient(to right, transparent 60%, #0c1020 100%),
                linear-gradient(to top, #080c18 0%, transparent 25%),
                linear-gradient(to bottom, #0c1020 0%, transparent 20%)
              `,
            }}
          />
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-[50px] glow-breathe"
              style={{ background: "rgba(147,51,234,0.15)" }}
            />
            <div
              className="absolute top-1/3 right-1/4 w-12 h-12 rounded-full blur-[25px] glow-breathe"
              style={{
                background: "rgba(168,85,247,0.10)",
                animationDelay: "1.5s",
              }}
            />
            <div
              className="absolute bottom-1/4 left-1/4 w-10 h-10 rounded-full blur-[20px] glow-breathe"
              style={{
                background: "rgba(34,197,94,0.08)",
                animationDelay: "2.5s",
              }}
            />
          </div>

          <div
            className="relative z-10 flex items-center justify-center agent-float"
            style={{ width: "90%", height: "90%" }}
          >
            <img
              src={AGENT_IMAGE_URL_ACTIVE}
              alt="AI Agent"
              className="w-full h-full object-contain"
              style={{
                filter:
                  "drop-shadow(0 0 14px rgba(147,51,234,0.4)) drop-shadow(0 0 28px rgba(34,197,94,0.15)) drop-shadow(0 4px 10px rgba(0,0,0,0.5)) contrast(1.08) saturate(1.05)",
              }}
            />
          </div>

          <div className="relative w-16 h-2 -mt-1 z-10">
            <div
              className="absolute inset-0 rounded-full energy-pulse"
              style={{
                background:
                  "radial-gradient(ellipse, rgba(147,51,234,0.4) 0%, rgba(168,85,247,0.18) 40%, transparent 70%)",
              }}
            />
          </div>

          {/* <div
            className="relative z-10 mt-2 text-[10px] tracking-wider uppercase"
            style={{ color: "rgba(34,197,94,0.35)" }}
          >
            {t("homePage.myAgentHomeActive.enterAnalysis")}
          </div> */}
        </button>

        <div
          className="flex-1 py-4 pr-4 pl-1 space-y-3"
          style={{ minWidth: 0 }}
        >
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-black text-base tracking-wide">
                {agent.name}
              </span>
              <span
                className="text-[10px] px-1.5 py-[2px] rounded font-semibold"
                style={{
                  background: `${C.amber}12`,
                  color: C.amber,
                  border: `1px solid ${C.amber}20`,
                }}
              >
                {tierLabel}
              </span>
              <span
                className="flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-bold ml-auto"
                style={{
                  background: "rgba(34,197,94,0.06)",
                  color: C.green,
                  border: "1px solid rgba(34,197,94,0.10)",
                }}
              >
                <div
                  className="w-1 h-1 rounded-full animate-pulse"
                  style={{ background: C.green }}
                />
                {t("homePage.myAgentCard.statusRunning")}
              </span>
            </div>
            <div className="mt-0.5 text-[10px]" style={{ color: C.green }}>
              {t("homePage.myAgentHomeActive.strategyExecuting")}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1">
            <div
              className="text-center py-1.5 rounded-lg"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-[10px] mb-0.5" style={{ color: C.text }}>
                {t("homePage.benefit.custodyBoost")}
              </div>
              <div
                className="text-sm font-bold mono-num"
                style={{ color: C.primary }}
              >
                {`${agent.aiManageAddSpeedBp / 100}%`}
              </div>
            </div>
            <div
              className="text-center py-1.5 rounded-lg"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-[10px] mb-0.5" style={{ color: C.text }}>
                {t("homePage.benefit.releaseMultiple")}
              </div>
              <div
                className="text-sm font-bold mono-num"
                style={{ color: C.cyan }}
              >
                {`${agent.exitMultiple / 100}x`}
              </div>
            </div>
            <div
              className="text-center py-1.5 rounded-lg"
              style={{
                background: "rgba(12,16,32,0.5)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-[10px] mb-0.5" style={{ color: C.text }}>
                {t("homePage.benefit.aifToken", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </div>
              <div
                className="text-sm font-bold mono-num"
                style={{ color: C.amber }}
              >
                {aifValueDisplay}
              </div>
            </div>
            {agent.giftGiveLevel > 0 ? (
              <div
                className="text-center py-1.5 rounded-lg"
                style={{
                  background: "rgba(12,16,32,0.5)",
                  border: "1px solid rgba(100,140,200,0.04)",
                }}
              >
                <div className="text-[10px] mb-0.5" style={{ color: C.text }}>
                  {t("homePage.benefit.teamLevel")}
                </div>
                <div
                  className="text-sm font-bold mono-num"
                  style={{ color: C.cyan }}
                >
                  {`C${agent.giftGiveLevel}`}
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex gap-2">
            <div
              className="flex-1 rounded-lg p-2"
              style={{
                background: "rgba(34,197,94,0.02)",
                border: "1px solid rgba(34,197,94,0.04)",
              }}
            >
              <div className="text-[10px]" style={{ color: C.text }}>
                {t("homePage.myAgentActiveDetailDialog.todayEarned")}
              </div>
              <div
                className="text-base font-black mono-num leading-tight mt-0.5"
                style={{ color: C.green }}
              >
                +{todayEarnedU} U
              </div>
            </div>
            <div
              className="flex-1 rounded-lg p-2"
              style={{
                background: "rgba(12,16,32,0.4)",
                border: "1px solid rgba(100,140,200,0.04)",
              }}
            >
              <div className="text-[10px]" style={{ color: C.text }}>
                {t("homePage.myAgentActiveDetailDialog.dailyRate")}
              </div>
              <div className="text-base font-bold mono-num leading-tight mt-0.5">
                {/* {dailyRate} */}
                {DAY_RAT_MAP[agent.name]}
              </div>
            </div>
          </div>

          {/* <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: `1px solid ${C.border}`,
                color: C.muted,
              }}
              onClick={e => {
                e.stopPropagation();
                onAddress(agent);
              }}
            >
              <MapPin size={10} />
              {t("homePage.myAgentCard.deliveryAddress")}
            </button>
            <button
              type="button"
              className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: `1px solid ${C.border}`,
                color: C.muted,
              }}
              onClick={e => {
                e.stopPropagation();
                onIncomeHistory(agent);
              }}
            >
              <History size={10} />
              {t("homePage.myAgentCard.incomeHistory")}
            </button>
            <button
              type="button"
              className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: `1px solid ${C.border}`,
                color: C.muted,
              }}
              onClick={e => {
                e.stopPropagation();
                onDeliveryStatus(agent);
              }}
            >
              <Truck size={10} />
              {t("homePage.myAgentCard.deliveryStatus")}
            </button>
          </div> */}

          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition-all active:scale-95"
              style={{
                background: "rgba(100,140,200,0.04)",
                border: "1px solid rgba(100,140,200,0.06)",
                color: "rgba(136,146,168,0.7)",
              }}
              onClick={e => {
                e.stopPropagation();
                openActiveDetail(agent.agentId);
              }}
            >
              <Info size={10} />
              {t("homePage.myAgentCard.viewDetail")}
            </button>
            <button
              type="button"
              className="flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 btn-primary transition-all active:scale-95"
              onClick={e => {
                e.stopPropagation();
                showFeatureNotAvailable();
              }}
            >
              <Zap size={10} />
              {t("homePage.myAgentHomeActive.enterAnalysis")}
            </button>
          </div>

          <button
            type="button"
            className="w-full py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
            style={{
              background: `${C.primary}10`,
              color: C.primary,
              border: `1px solid ${C.primary}20`,
            }}
            onClick={e => {
              e.stopPropagation();
              onGoManage();
            }}
          >
            <ArrowRight size={10} />
            {t("homePage.myAgents.goManage")}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
